import React, { useCallback } from 'react';
import { Card, Typography } from 'antd';

const { Text } = Typography;

// Les 8 directions cardinales
const DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
export type Direction = typeof DIRECTIONS[number];

interface WindRoseProps {
  selectedOrientations: string[];
  onChange: (orientations: string[]) => void;
  title?: string;
}

// Configuration des secteurs SVG dans l'ordre du SVG fourni
const SECTOR_CONFIG = [
  { direction: 'E', path: 'm370.28 168.82-142.14 58.18 142.14 58.185h0.005l83.722-58.185z', textX: 350, textY: 247.58344, hasText: true },
  { direction: 'W', path: 'm83.727 168.82 142.14 58.18-142.14 58.185h-0.0046l-83.725-58.18z', textX: 56, textY: 247.58344, hasText: true },
  { direction: 'S', path: 'm285.19 370.28-58.18-142.14-58.185 142.14v0.005l58.185 83.722z', textX: 206.10295, textY: 390.00714, hasText: true },
  { direction: 'N', path: 'm285.19 83.727-58.18 142.14-58.19-142.14v-0.005l58.19-83.725z', textX: 205.0424, textY: 105.007141, hasText: true },
  { direction: 'SE', path: 'm369.46 287.17-141.65-59.371 59.368 141.65 0.002 0.002 100.34 18.058z', textX: 320, textY: 350, hasText: false },
  { direction: 'NW', path: 'm166.83 84.55 59.371 141.65-141.65-59.368-0.0032-0.002l-18.058-100.34z', textX: 90, textY: 60, hasText: false },
  { direction: 'SW', path: 'm166.83 369.46 59.371-141.65-141.65 59.368-0.0032 0.002-18.058 100.34z', textX: 90, textY: 390, hasText: false },
  { direction: 'NE', path: 'm369.46 166.83-141.65 59.371 59.368-141.65 0.002-0.002 100.34-18.058z', textX: 320, textY: 60, hasText: false },
] as const;

const WindRose: React.FC<WindRoseProps> = ({
  selectedOrientations,
  onChange,
  title = 'Orientation'
}) => {
  const toggleOrientation = useCallback((direction: string) => {
    const newOrientations = selectedOrientations.includes(direction)
      ? selectedOrientations.filter(o => o !== direction)
      : [...selectedOrientations, direction];
    onChange(newOrientations);
  }, [selectedOrientations, onChange]);

  return (
    <Card size="small" style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
        <Text strong style={{ fontSize: '14px' }}>{title}</Text>

        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100"
          height="100"
          version="1.1"
          viewBox="0 0 454.00715 454.00714"
          style={{ cursor: 'pointer' }}
          className="input-orientation"
        >
          {SECTOR_CONFIG.map((config) => {
            const isSelected = selectedOrientations.includes(config.direction);

            return (
              <g key={config.direction}>
                <path
                  d={config.path}
                  fill={isSelected ? '#1890ff' : '#f0f0f0'}
                  stroke={isSelected ? '#1890ff' : '#d9d9d9'}
                  strokeWidth={isSelected ? '2' : '1'}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => toggleOrientation(config.direction)}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.setAttribute('fill', '#e6f7ff');
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.setAttribute('fill', '#f0f0f0');
                    }
                  }}
                />

                {/* Labels pour les directions principales uniquement */}
                {config.hasText && (
                  <text
                    y={config.textY}
                    x={config.textX}
                    fontSize="60"
                    fontWeight={isSelected ? 'bold' : 'normal'}
                    fill={isSelected ? '#fff' : '#595959'}
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {config.direction}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Indicateur de sélection */}
        {selectedOrientations.length > 0 && (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {selectedOrientations.length} orientation{selectedOrientations.length > 1 ? 's' : ''} sélectionnée{selectedOrientations.length > 1 ? 's' : ''}
          </Text>
        )}
      </div>
    </Card>
  );
};

export default WindRose;
